import React from 'react';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ChartData,
  LegendItem,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { dataLabelPlugin } from './DataLabelPlugin';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, dataLabelPlugin);

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const butterCocoaColor = {
  strongBlue: 'rgb(122, 103, 182)',
  lightBlue: '#AFC7EB',
  strongGreen: '#38BDDB',
  lightGreen: '#AFE5F1',
  purple: '#DA18C2',
  gray: '#bbb',
  white: 'rgba(255,255,255,0)',
};

const massCocoaColor = {
  strongBlue: '#3773CE',
  lightBlue: '#AFC7EB',
  strongGreen: '#38BDDB',
  lightGreen: '#AFE5F1',
  purple: '#DA18C2',
  gray: '#bbb',
  white: 'rgba(255,255,255,0)',
};

const currMonth = new Date().getMonth();
const getData = (cocoaColors: any, orders: number[], positions: number[], inventory: number[]) => ({
  labels: ['Stg', ...months.map((_, i) => months[(currMonth + i) % 12])],
  datasets: [
    {
      label: 'Physical',
      data: inventory,
      backgroundColor: cocoaColors.strongBlue,
      stack: 'exposure-1',
      borderColor: cocoaColors.gray,
      borderWidth: { left: 1 },
    },
    {
      label: 'Financial',
      data: [],
      backgroundColor: cocoaColors.lightBlue,
      stack: 'exposure-1',
      borderColor: cocoaColors.gray,
      borderWidth: { left: 1 },
    },
    {
      label: 'Ratio',
      data: [],
      backgroundColor: cocoaColors.purple,
      stack: 'exposure-1',
      borderColor: cocoaColors.gray,
      borderWidth: { left: 1 },
      borderRadius: { topRight: 1 },
    },
    {
      label: 'Total Exposure',
      data: getItemMinus(orders, inventory), //: getItemMinus(totalExp, butterPhis), // + butterFinan[i] + ratioPosition[i])),
      backgroundColor: cocoaColors.white,
      borderColor: cocoaColors.gray,
      borderWidth: 1, //{ top: 1, left: 1, right: 1 },
      borderRadius: { topLeft: 1 },
      stack: 'exposure-1',
      //barThickness: 20,
    },
    // {
    //   label: 'Butter Physical',
    //   data: butterPhis,
    //   backgroundColor: cocoaColors.strongBlue,
    //   stack: 'exposure-2',
    //   borderColor: cocoaColors.gray,
    //   borderWidth: { right: 1 },
    // },
    // {
    //   label: 'Butter Financial',
    //   data: butterFinan,
    //   backgroundColor: cocoaColors.lightBlue,
    //   stack: 'exposure-2',
    //   borderColor: cocoaColors.gray,
    //   borderWidth: { right: 1 },
    // },
    // {
    //   label: 'Beans Physical',
    //   data: beansPhisic,
    //   backgroundColor: cocoaColors.strongGreen,
    //   stack: 'exposure-2',
    //   borderColor: cocoaColors.gray,
    //   borderWidth: { right: 1 },
    // },
    // {
    //   label: 'Beans Financial',
    //   data: beansFinan,
    //   backgroundColor: cocoaColors.lightGreen,
    //   stack: 'exposure-2',
    //   borderColor: cocoaColors.gray,
    //   borderWidth: { right: 1 },
    //   borderRadius: { topLeft: 1 },
    // },
    // {
    //   label: 'Total Exposure',
    //   data: totalExp.map((exp, i) => exp - (butterPhis[i] || 0)), // + butterFinan[i] + beansFinan[i] + beansPhisic[i])),
    //   backgroundColor: cocoaColors.white,
    //   borderColor: cocoaColors.gray,
    //   borderWidth: { top: 1, right: 1,  },
    //   borderRadius: { topRight: 1 },
    //   stack: 'exposure-2',
    //   barThickness: 20,
    // },
  ].map(({ ...a }) => ({ ...a, barThickness: 40 })),
});

const getItemMinus = (total: number[], phisic: number[]) =>
  total.map((exp, i) => (exp < phisic[i] ? 0 : Math.abs(exp - (phisic[i] || 0))));

const getOptions = (hasRatio: boolean, orders: number[], inventory: number[]) => ({
  responsive: true,
  plugins: {
    legend: {
      position: 'bottom' as const,
      labels: {
        filter: (legendItem: LegendItem, chartData: ChartData<'bar', number[], string>): boolean => {
          const dataset = chartData.datasets[legendItem.datasetIndex || 0];
          return dataset.stack === 'exposure-1' || (!!dataset.label?.startsWith('Ratio') && hasRatio);
        },
      },
    },
    tooltip: {
      mode: 'index' as const,
      intersect: false,

      callbacks: {
        label: (ctx: any) => {
          //override label of orders to allow hiding the bar, and showing value in tooltip

          const dsIndex = ctx.datasetIndex;
          const ds = ctx.chart.data.datasets[dsIndex];
          const label = ds?.label ?? '';
          const val = ctx.parsed?.y ?? ctx.parsed?.x ?? ctx.raw;

          return label + ': ' + (label === 'Total Exposure' ? orders[ctx.dataIndex] : val).toLocaleString();
        },
      },
    },
    dataLabelPlugin: {
      orders,
      inventory,
    },
  },
  interaction: {
    mode: 'nearest' as const,
    axis: 'x' as const,
    intersect: false,
  },
  scales: {
    x: {
      stacked: true,
      barPercentage: 1.0,
      categoryPercentage: 1.0,
      ticks: {
        padding: 0,
      },
    },
    y: {
      stacked: true,
    },
  },
  elements: {
    line: {
      tension: 0, // no curve
    },
  },
});

export const CocoaExposureChart: React.FC<{
  material: string;
  orders: number[];
  positions: number[];
  inventory: number[];
  hasRatio?: boolean;
}> = ({ material, orders, positions, inventory, hasRatio = false }) => {
  return (
    <Bar
      options={getOptions(hasRatio, orders, inventory)}
      data={getData(material === 'mass' ? massCocoaColor : butterCocoaColor, orders, positions, inventory)}
      plugins={[dataLabelPlugin]}
    />
  );
};
